import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, fmtMoney, dimensionSummary } from "@/lib/api";
import { ArrowLeft, Plus, Trash } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import ItemPicker from "@/components/ItemPicker";
import { toast } from "sonner";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

// Numeric fields arrive pre-filled (qty 1, prices 0). Selecting the value on
// focus means typing replaces it instead of appending to it.
const selectAll = (e) => e.target.select();

const newItem = () => ({
  description: "", catalog_item_id: null, item_description: "", qty: 1, unit: "pcs",
  cost_price: 0, cost_currency: "CNY",
  sell_price: 0, sell_currency: "PKR",
  supplier_id: "",
});

// Memoised so typing in one line does not re-render every other line.
const LineRow = memo(function LineRow({ it, i, suppliers, ids, onUpdate, onRemove }) {
  // Catalog ids on the other lines (own id excluded) for the "Added" marker.
  const own = it.catalog_item_id;
  const addedIds = useMemo(() => {
    const rest = [...ids];
    const k = rest.indexOf(own);
    if (k >= 0) rest.splice(k, 1);
    return new Set(rest);
  }, [ids, own]);
  return (
    <tr className="block md:table-row" data-testid={`line-item-${i}`}>
      <td className="block md:table-cell">
        <div className="border border-zinc-200 rounded-sm p-3 mb-3">
          <div className="label-mini pb-2">Item {i + 1}</div>
          {/* Line 1: name + description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="label-mini mb-1">Item Name</div>
            <ItemPicker
              value={it.description}
              testId={`line-desc-${i}`}
              placeholder="Type to search catalog or enter custom"
              className="rounded-sm h-11 sm:h-9"
              addedIds={addedIds}
              onChange={(text) => onUpdate(i, { description: text, catalog_item_id: null })}
              onSelect={(picked) => onUpdate(i, {
                catalog_item_id: picked.id,
                description: picked.name
                  + (dimensionSummary(picked) ? ` (${dimensionSummary(picked)})` : ""),
                item_description: picked.description || "",
                unit: picked.unit || it.unit,
                cost_price: picked.default_cost ?? it.cost_price,
                cost_currency: picked.default_cost_currency || it.cost_currency,
                sell_price: picked.default_sell ?? it.sell_price,
                sell_currency: picked.default_sell_currency || it.sell_currency,
                supplier_id: picked.supplier_id || it.supplier_id,
              })}
            />
          </div>
          <div>
            <div className="label-mini mb-1">Description</div>
            <textarea
              value={it.item_description}
              onChange={(e) => onUpdate(i, { item_description: e.target.value })}
              placeholder="Shown under name on quotation/invoice (optional)"
              data-testid={`line-item-desc-${i}`}
              className="w-full border border-zinc-200 rounded-sm p-1.5 text-base sm:text-xs text-zinc-500 h-11 sm:h-9"
            />
          </div>
          </div>
          {/* Line 2: qty, unit, prices, supplier */}
          <div className="grid grid-cols-2 md:grid-cols-[5rem_6rem_1fr_1fr_1fr_auto] gap-3 mt-3 items-end">
          <div>
            <div className="label-mini mb-1">Qty</div>
            <Input type="number" inputMode="decimal" onFocus={selectAll} value={it.qty} onChange={(e) => onUpdate(i, { qty: e.target.value })} className="rounded-sm h-11 sm:h-9 font-mono" />
          </div>
          <div>
            <div className="label-mini mb-1">Unit</div>
            <Input value={it.unit} onChange={(e) => onUpdate(i, { unit: e.target.value })} className="rounded-sm h-11 sm:h-9" />
          </div>
          <div>
            <div className="label-mini mb-1">Purchase Price</div>
            <div className="flex gap-1">
              <Input type="number" inputMode="decimal" onFocus={selectAll} value={it.cost_price} onChange={(e) => onUpdate(i, { cost_price: e.target.value })} className="rounded-sm h-11 sm:h-9 font-mono" />
              <Select value={it.cost_currency} onValueChange={(v) => onUpdate(i, { cost_currency: v })}>
                <SelectTrigger className="rounded-sm h-11 sm:h-9 w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PKR">PKR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CNY">CNY</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="label-mini mb-1">Selling Price</div>
            <div className="flex gap-1">
              <Input type="number" inputMode="decimal" onFocus={selectAll} value={it.sell_price} onChange={(e) => onUpdate(i, { sell_price: e.target.value })} className="rounded-sm h-11 sm:h-9 font-mono" />
              <Select value={it.sell_currency} onValueChange={(v) => onUpdate(i, { sell_currency: v })}>
                <SelectTrigger className="rounded-sm h-11 sm:h-9 w-20"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PKR">PKR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="CNY">CNY</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <div className="label-mini mb-1">Default Supplier</div>
            <Select value={it.supplier_id || ""} onValueChange={(v) => onUpdate(i, { supplier_id: v })}>
              <SelectTrigger className="rounded-sm h-11 sm:h-9"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={() => onRemove(i)}
            className="text-zinc-500 hover:text-red-600 flex items-center gap-1.5 p-2 md:text-zinc-400 md:mb-0.5"
            data-testid={`remove-line-${i}`}
          >
            <Trash size={14} />
            <span className="text-xs md:hidden">Remove</span>
          </button>
          </div>
        </div>
      </td>
    </tr>
  );
});

export default function OrderCreate() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [rates, setRates] = useState({ PKR: 1, USD: 280, CNY: 38 });
  const [editStatus, setEditStatus] = useState(null);

  const [customerId, setCustomerId] = useState("");
  const [brandId, setBrandId] = useState("abwaa");
  const [shippingMethod, setShippingMethod] = useState("air");
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingChargedToCustomer, setShippingChargedToCustomer] = useState(true);
  const [showShippingOnDocument, setShowShippingOnDocument] = useState(true);
  const [gstPercent, setGstPercent] = useState(0);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().slice(0, 10));
  const [expectedDelivery, setExpectedDelivery] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
const [customerNotes, setCustomerNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [items, setItems] = useState([newItem()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
    api.get("/settings").then((r) => {
      setRates(r.data.rates || rates);
      if (!isEdit) setGstPercent(r.data.default_gst_percent || 0);
    });
    api.get("/invoices/brands").then((r) => {
      setBrands(r.data || []);
      if (!isEdit && r.data?.[0]?.id) setBrandId(r.data[0].id);
    }).catch(() => {});
    if (isEdit) {
      api.get(`/orders/${editId}`).then(({ data: o }) => {
        setCustomerId(o.customer_id || "");
        setBrandId(o.brand_id || "abwaa");
        setShippingMethod(o.shipping_method || "air");
        setShippingCost(o.shipping_cost || 0);
        setShippingChargedToCustomer(o.shipping_charged_to_customer ?? true);
        setShowShippingOnDocument(o.show_shipping_on_document ?? true);
        setGstPercent(o.gst_percent || 0);
        if (o.order_date) setOrderDate(o.order_date.slice(0, 10));
        setExpectedDelivery((o.expected_delivery || "").slice(0, 10));
        setReferenceNo(o.reference_no || "");
        setCustomerNotes(o.customer_notes || "");
        setInternalNotes(o.internal_notes || "");
        setItems((o.items || []).length ? o.items : [newItem()]);
        setEditStatus(o.status);
      }).catch(() => toast.error("Failed to load order"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, isEdit]);

  // Stable callbacks (functional setState) so memoised rows are not re-rendered.
  const updateItem = useCallback((i, patch) => {
    setItems((prev) => prev.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  }, []);
  const removeItem = useCallback((i) => setItems((prev) => prev.filter((_, idx) => idx !== i)), []);
  // Only changes when a line's catalog item changes, not on every keystroke.
  // Blank lines are left out so adding an empty line does not re-render the others.
  const idsKey = items.map((x) => x.catalog_item_id).filter(Boolean).join("|");
  const ids = useMemo(() => (idsKey ? idsKey.split("|") : []), [idsKey]);
  const addItem = () => setItems((prev) => [...prev, newItem()]);

  // Totals
  const totals = items.reduce((acc, it) => {
    const sRate = rates[it.sell_currency] || 1;
    const cRate = rates[it.cost_currency] || 1;
    acc.sell += Number(it.qty || 0) * Number(it.sell_price || 0) * sRate;
    acc.cost += Number(it.qty || 0) * Number(it.cost_price || 0) * cRate;
    return acc;
  }, { sell: 0, cost: 0 });
  const ship = Number(shippingCost || 0);
  const sellWithShip = totals.sell + (shippingChargedToCustomer ? ship : 0);
  const costWithShip = totals.cost + (shippingChargedToCustomer ? 0 : ship);
  const gst = sellWithShip * Number(gstPercent || 0) / 100;
  const grandTotal = sellWithShip + gst;
  const margin = sellWithShip - costWithShip;   // GST is collected, not earned

  const supplierIds = Array.from(new Set(items.map((i) => i.supplier_id).filter(Boolean)));

  const submit = async (status) => {
    if (!customerId) { toast.error("Select customer"); return; }
    if (items.some((i) => !i.description)) { toast.error("All items need description"); return; }
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId,
        brand_id: brandId,
        supplier_ids: supplierIds,
        order_date: orderDate,
        expected_delivery: expectedDelivery || null,
        shipping_method: shippingMethod,
        tracking_no: null,
        shipping_cost: ship,
        shipping_charged_to_customer: shippingChargedToCustomer,
        gst_percent: Number(gstPercent || 0),
        show_shipping_on_document: showShippingOnDocument,
        reference_no: referenceNo.trim() || null,
        customer_notes: customerNotes,
        internal_notes: internalNotes,
        assigned_sales: null,
        assigned_ops: null,
        items,
        status,
      };
      const { data } = isEdit
        ? await api.put(`/orders/${editId}`, payload)
        : await api.post("/orders", payload);
      toast.success(`${data.code} ${isEdit ? "updated" : "created"}`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="order-create-page">
      <button onClick={() => navigate(-1)} className="text-xs label-mini flex items-center gap-1 hover:text-black">
        <ArrowLeft size={12} /> Back
      </button>

      <div className="flex items-end justify-between">
        <div>
          <div className="label-mini">{isEdit ? "Edit Order" : "New Order / Quotation"}</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">
            {isEdit ? "Edit Order" : "Create Quotation"}
          </h1>
          {isEdit && editStatus && editStatus !== "quotation" && editStatus !== "cancelled" && (
            <div className="text-xs text-amber-700 mt-1">
              This order is already <strong>{editStatus}</strong> (billed). Changing the total will create an adjustment ledger entry.
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5 md:col-span-2 space-y-3">
          <h3 className="label-mini">Order Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Customer" required>
              <Select value={customerId} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-sm h-11 sm:h-9" data-testid="select-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Issue under (Brand)" required>
              <Select value={brandId} onValueChange={setBrandId}>
                <SelectTrigger className="rounded-sm h-11 sm:h-9" data-testid="select-brand">
                  <SelectValue placeholder="Select brand" />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((b) => (
                    <SelectItem key={b.id} value={b.id} data-testid={`brand-option-${b.id}`}>{b.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Shipping Method">
              <Select value={shippingMethod} onValueChange={setShippingMethod}>
                <SelectTrigger className="rounded-sm h-11 sm:h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="air">Air Freight</SelectItem>
                  <SelectItem value="sea">Sea Freight</SelectItem>
                  <SelectItem value="local_courier">Local Courier</SelectItem>
                  <SelectItem value="hand_delivery">Hand Delivery</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Client Reference / RFQ No.">
              <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="e.g. RFQ-2041" className="rounded-sm h-11 sm:h-9 font-mono" />
            </Field>
            <Field label="Order Date">
              <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="rounded-sm h-11 sm:h-9" />
            </Field>
            <Field label="Expected Delivery">
              <Input type="date" value={expectedDelivery} onChange={(e) => setExpectedDelivery(e.target.value)} className="rounded-sm h-11 sm:h-9" />
            </Field>
            <Field label="Shipping Cost (PKR)">
              <Input type="number" inputMode="decimal" onFocus={selectAll} value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} className="rounded-sm h-11 sm:h-9 font-mono" />
            </Field>
            <Field label="GST %">
              <Input
                type="number" min="0" max="100" step="0.01"
                inputMode="decimal" onFocus={selectAll}
                value={gstPercent}
                onChange={(e) => setGstPercent(e.target.value)}
                className="rounded-sm h-11 sm:h-9 font-mono"
                data-testid="gst-percent-input"
                placeholder="0 = no GST"
              />
            </Field>
            <Field label="Shipping Charged To">
              <Select value={shippingChargedToCustomer ? "customer" : "business"} onValueChange={(v) => setShippingChargedToCustomer(v === "customer")}>
                <SelectTrigger className="rounded-sm h-11 sm:h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer">Customer (added to invoice)</SelectItem>
                  <SelectItem value="business">Business (absorbed)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Freight on Document">
              <label className="flex items-center gap-2 h-11 sm:h-9 text-sm">
                <Checkbox
                  checked={showShippingOnDocument}
                  onCheckedChange={(v) => setShowShippingOnDocument(v === true)}
                  data-testid="show-shipping-on-document-checkbox"
                />
                Show freight line on quotation/invoice
              </label>
            </Field>
          </div>
          <Field label="Customer-Facing Notes">
            <textarea value={customerNotes} onChange={(e) => setCustomerNotes(e.target.value)} className="w-full border border-zinc-200 rounded-sm p-2 text-base sm:text-sm h-16" />
          </Field>
          <Field label="Internal Notes (private)">
            <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} className="w-full border border-zinc-200 rounded-sm p-2 text-base sm:text-sm h-16" />
          </Field>
        </div>

        <div className="bg-white p-5">
          <h3 className="label-mini">Pricing Summary</h3>
          <div className="space-y-3 mt-3">
            <Row label="Subtotal" value={fmtMoney(sellWithShip)} />
            {Number(gstPercent) > 0 && (
              <>
                <Row label={`GST @ ${Number(gstPercent)}%`} value={fmtMoney(gst)} muted />
                <Row label="Total Payable" value={fmtMoney(grandTotal)} strong />
              </>
            )}
            <Row label="Total Cost" value={fmtMoney(costWithShip)} muted />
            <div className="border-t border-zinc-200 pt-3">
              <Row label="Gross Margin" value={fmtMoney(margin)} strong color={margin >= 0 ? "#065F46" : "#B91C1C"} />
              <div className="text-[11px] text-zinc-500 mt-1">
                Margin %: <span className="font-mono">{sellWithShip > 0 ? ((margin / sellWithShip) * 100).toFixed(1) : "0"}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-sharp p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="label-mini">Line Items</h3>
        </div>
        <div className="md:overflow-x-auto">
          <table className="w-full text-sm block md:table">
            <thead className="hidden">
              <tr className="text-left label-mini border-b border-zinc-200">
                <th className="py-2">Description</th>
                <th className="py-2 w-16">Qty</th>
                <th className="py-2 w-20">Unit</th>
                <th className="py-2 w-32">Cost</th>
                <th className="py-2 w-32">Selling</th>
                <th className="py-2 w-40">Supplier</th>
                <th className="py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {items.map((it, i) => (
                <LineRow
                  key={i} it={it} i={i} suppliers={suppliers} ids={ids}
                  onUpdate={updateItem} onRemove={removeItem}
                />
              ))}
            </tbody>
          </table>
        </div>
        <Button onClick={addItem} variant="outline" className="h-8 px-3 mt-3 rounded-sm border-black text-xs" data-testid="add-line-item">
          <Plus size={12} className="mr-1" /> Add Item
        </Button>
        <div className="text-[11px] text-zinc-500 mt-3">
          Rates: 1 USD = {rates.USD} PKR · 1 CNY = {rates.CNY} PKR. Configurable in Settings.
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(isEdit ? `/orders/${editId}` : "/orders")} className="rounded-sm h-11 sm:h-9 w-full sm:w-auto">Cancel</Button>
        {isEdit ? (
          <Button onClick={() => submit(editStatus)} disabled={saving} className="rounded-sm h-11 sm:h-9 w-full sm:w-auto bg-black text-white hover:bg-zinc-800" data-testid="save-edit-btn">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        ) : (
          <>
            <Button onClick={() => submit("quotation")} disabled={saving} className="rounded-sm h-11 sm:h-9 w-full sm:w-auto bg-white text-black border border-black hover:bg-zinc-50" data-testid="save-as-quotation">
              Save as Quotation
            </Button>
            <Button onClick={() => submit("confirmed")} disabled={saving} className="rounded-sm h-11 sm:h-9 w-full sm:w-auto bg-black text-white hover:bg-zinc-800" data-testid="save-as-confirmed">
              Save &amp; Confirm Order
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div>
    <label className="label-mini">{label}{required && <span className="text-red-600">*</span>}</label>
    <div className="mt-1">{children}</div>
  </div>
);

const Row = ({ label, value, strong, muted, color }) => (
  <div className="flex items-center justify-between">
    <span className={`text-xs ${muted ? "text-zinc-500" : "text-zinc-700"}`}>{label}</span>
    <span className={`font-mono tnum ${strong ? "text-base font-bold" : "text-sm"}`} style={color ? { color } : {}}>{value}</span>
  </div>
);
